import * as React from "react";
import * as ReactDom from "react-dom";

import l10n from "diy/util/l10n";

import Api from "../Api";
import Layout from "../definitions/Layout";
import Severity from "../definitions/Severity";
import {NotificationConfig} from "../definitions/Config";
import NotificationActionButton from "./NotificationActionButton";
import NotificationDismissButton from "./NotificationDismissButton";

import {colors} from "material-ui/styles";
import mwsSpacings = require("diy/material-ui/spacings");
import Avatar from "material-ui/Avatar";

import ToggleStarIcon from "material-ui/svg-icons/toggle/star";

import {Card, CardText, CardActions} from "material-ui/Card";

interface NotificationIconList {
    [key: string]: any;
}

interface NotificationProps {

    /** Configuration object */
    config: NotificationConfig;

    /** Bubble rendering vs. standard rendering */
    layout?: Layout;

    /** CSS Class */
    className?: string;
}

export default class Notification extends React.Component<NotificationProps, {}> {

    getStyles() {
        let smallSpacing = mwsSpacings.small + "px";
        let mediumSpacing = mwsSpacings.medium + "px";

        let styles: {
            root: React.CSSProperties;
            container: React.CSSProperties;
            content: React.CSSProperties;
            title: React.CSSProperties;
            icon: React.CSSProperties;
            actions: React.CSSProperties;
            button: React.CSSProperties;
        } = {
            root: {
                pointerEvents: "all"
            },
            container: {
                marginBottom: mediumSpacing
            },
            content: {
                display: "flex",
                background: colors.white,
                padding: mediumSpacing + " " + mediumSpacing + " " + mediumSpacing + " " + smallSpacing
            },
            title: {
                fontWeight: 600,
                marginBottom: smallSpacing
            },
            icon: {
                marginTop: "-" + smallSpacing,
                marginRight: smallSpacing,
                color: colors.blue500
            },
            actions: {
                textAlign: "right"
            },
            button: {
                marginLeft: "10px",
                marginRight: "0px"
            }
        };
        switch (this.props.config.severity) {
            case Severity.HIGH:
                styles.content.color = colors.white;
                styles.content.background = colors.blue500;
                styles.icon.color = colors.white;
                break;

            case Severity.NORMAL:
            default:
                styles.actions.borderTop = "1px solid #dddddd";
                break;
        }
        if (this.props.layout === Layout.Bubble) {
            styles.root.position = "relative";
            styles.root.transition = "opacity 400ms, margin 400ms, visibility 0s";
            styles.root.visibility = "hidden";
            styles.root.opacity = 0;

        } else {
            styles.root.marginTop = mediumSpacing;
            styles.root.marginLeft = mediumSpacing;
            styles.root.marginRight = mediumSpacing;
        }
        return styles;
    }

    /**
     * Component Function reachable thanks to the Transition Wrapper
     * (See file BubbleManager.tsx)
     */
    componentDidAppear() {
        /**
         * Without a small timeout on showing, the animation is happening during the HTML generation
         * so it doesn't really look animated - it seems to show all of the sudden.
         */
        let animationEffectDelay = 500;

        setTimeout(() => {

            /** Element is nicely animated with CSS transitions on showing */
            if (this.props.layout === Layout.Bubble) {
                let rootElement = ReactDom.findDOMNode(this) as HTMLElement;
                rootElement.style.visibility = "visible";
                rootElement.style.opacity = "1";
            }

            /**
             * If the configuration has `autoHide` = true,
             * Bubble dismisses itself after `hideDelay` ms
             */
            Api.schedule(() => {
                let type = this.props.config.type;
                let autoHide = this.props.config.bubbleOptions.autoHide;
                let hideDelay = this.props.config.bubbleOptions.hideDelay;

                if (autoHide === true && !Api.isPaused()) {
                    setTimeout(() => {
                        Api.pushOne({
                            type: type,
                            dismissed: true
                        });
                    }, hideDelay);
                }
            });
        }, animationEffectDelay);
    }

    componentDidMount() {
        if (typeof this.props.config.onShow === "function") {
            this.props.config.onShow();
        }
    }

    render() {
        let styles = this.getStyles();
        let config = this.props.config;
        let layout = (typeof this.props.layout !== "undefined") ? this.props.layout : Layout.Card;

        /** Load Data Test Id for Selenium tests */
        let testId = "notification-" + config.testId;

        /** Load given Icon ; if none load the standard SVG Icon */
        let Icon = this.props.config.icon || ToggleStarIcon;

        /** Add a "bubble" CSS class for the Bubble Layout */
        let className = (this.props.className ? this.props.className : "")
            + (layout === Layout.Bubble ? " notification-bubble" : "");

        /**
         * A "dismiss" on the BUBBLE dismisses the Notification,
         * A "dismiss" on the CARD deleted the Notification
         */
        let isDismissDeletion = (layout === Layout.Card);

        /** Show dismiss button ONLY if "closable" */
        let showDismissAction = (
            (layout === Layout.Bubble && config.bubbleOptions.closable)
            || (layout === Layout.Card && config.cardOptions.closable)
        );

        /** Show action button if one exists */
        let showMainAction = config.mainAction;

        /** Don't show the actions bar when no action is being displayed */
        let actionStyles = (showMainAction || showDismissAction)
            ? styles.actions
            : { display: "none" };

        /** Default button text for "dismiss", depending on the context */
        let defaultDismissLabel = "";
        if (layout === Layout.Bubble) {
            defaultDismissLabel = l10n("notifications.dismiss");
        } else {
            defaultDismissLabel = l10n("notifications.delete");
        }

        /** Rendered only if still exists */
        if (!config.deleted) {
            return (
                <div className={className} data-test-id={testId} style={styles.root}>
                    <Card style={styles.container}>
                        <CardText style={styles.content}>
                            <Avatar size={40}
                                    icon={<Icon />}
                                    style={styles.icon}
                                    color={styles.icon.color}
                                    backgroundColor="transparent" />

                            <div>
                                {config.title
                                    ? <div style={styles.title}>{config.title}</div>
                                    : ""}
                                <div dangerouslySetInnerHTML={{__html: config.content}}></div>
                            </div>
                        </CardText>

                        <CardActions style={actionStyles}>
                            {showDismissAction
                                ? <NotificationDismissButton
                                      type={config.type}
                                      label={config.dismissAction
                                          ? config.dismissAction.label
                                          : defaultDismissLabel}
                                      onClick={config.dismissAction
                                          ? config.dismissAction.onClick
                                          : null}
                                      testId={config.dismissAction
                                          ? config.dismissAction.testId
                                          : ""}
                                      isDismissDeletion={isDismissDeletion}
                                      style={styles.button} />
                                : ""}
                            {showMainAction
                                ? <NotificationActionButton
                                      type={config.type}
                                      label={config.mainAction.label}
                                      onClick={config.mainAction.onClick}
                                      testId={config.mainAction.testId}
                                      trackingId={config.trackingId}
                                      style={styles.button} />
                                : ""}
                        </CardActions>
                    </Card>
                </div>
            );
        } else {
            return null;
        }
    }
}



/** WEBPACK FOOTER **
 ** ./build/js/~/tslint-loader!./source/cdn/document_root/s/diy/toolbar/notificationNG/components/Notification.tsx
 **/